import type * as GeoJSON from "geojson";

export type BoundaryFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Geometry,
  {
    geographyCode: string;
    name: string;
    value?: number | null;
    isSuppressed?: boolean;
    qualityFlag?: string | null;
    [key: string]: unknown;
  }
>;

export type BoundaryChunkManifest = {
  geographyLevel: string;
  version: string;
  chunkCount: number;
  totalFeatures: number;
  chunks: {
    fileName: string;
    featureCount: number;
    sizeBytes: number;
  }[];
};

const boundaryManifestPath = (geographyLevel: string) => {
  if (geographyLevel === "local-authority") {
    return "/data/lad-2024-boundaries-manifest.json";
  }

  return `/data/${geographyLevel}-2024-boundaries-manifest.json`;
};

const boundaryChunkPath = (chunkFileName: string) => `/data/${chunkFileName}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  const data: unknown = await response.json();
  return data;
}

function isBoundaryChunkManifest(value: unknown): value is BoundaryChunkManifest {
  if (!isRecord(value)) return false;
  if (typeof value.geographyLevel !== "string") return false;
  if (typeof value.version !== "string") return false;
  if (typeof value.chunkCount !== "number") return false;
  if (typeof value.totalFeatures !== "number") return false;
  if (!Array.isArray(value.chunks)) return false;
  return value.chunks.every(
    (chunk: unknown) =>
      isRecord(chunk) &&
      typeof chunk.fileName === "string" &&
      typeof chunk.featureCount === "number" &&
      typeof chunk.sizeBytes === "number",
  );
}

function isBoundaryFeature(feature: unknown): feature is BoundaryFeatureCollection["features"][number] {
  if (!isRecord(feature)) return false;
  if (!isRecord(feature.geometry)) return false;
  if (typeof feature.geometry.type !== "string") return false;
  if (!Array.isArray(feature.geometry.coordinates)) return false;
  if (!isRecord(feature.properties)) return false;
  if (typeof feature.properties.geographyCode !== "string") return false;
  return typeof feature.properties.name === "string";
}

function isBoundaryFeatureCollection(value: unknown): value is BoundaryFeatureCollection {
  if (!isRecord(value)) return false;
  if (!Array.isArray(value.features)) return false;
  return value.features.every((feature: unknown) => isBoundaryFeature(feature));
}

// Module-level cache of parsed boundary collections keyed by geography level.
// React Query caches the network response but still re-runs JSON.parse on a
// cache miss; keeping the parsed FeatureCollection here avoids the main-thread
// parse cost (several MB) when a level is re-entered or the tab is restored.
const parsedBoundaryCache = new Map<string, Promise<BoundaryFeatureCollection>>();

export async function loadBoundaryFeatureCollection(geographyLevel: string): Promise<BoundaryFeatureCollection> {
  const cached = parsedBoundaryCache.get(geographyLevel);
  if (cached) return cached;

  const promise = (async () => {
    const manifestData: unknown = await fetchJson(boundaryManifestPath(geographyLevel));
    if (!isBoundaryChunkManifest(manifestData)) {
      throw new Error(`Invalid boundary manifest for ${geographyLevel}`);
    }

    const chunkResults = await Promise.all(
      manifestData.chunks.map((chunk) => fetchJson(boundaryChunkPath(chunk.fileName))),
    );

    const features: BoundaryFeatureCollection["features"] = [];
    for (const chunkData of chunkResults) {
      if (!isBoundaryFeatureCollection(chunkData)) {
        throw new Error(`Invalid boundary chunk for ${geographyLevel}`);
      }
      features.push(...chunkData.features);
    }

    return {
      type: "FeatureCollection" as const,
      features,
    };
  })();

  parsedBoundaryCache.set(geographyLevel, promise);
  try {
    return await promise;
  } catch (error) {
    parsedBoundaryCache.delete(geographyLevel);
    throw error;
  }
}
